import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { uploadBusinessLogo } from '../services/storageService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import Input from '../components/Input';
import PlaceSearchInput from '../components/PlaceSearchInput'; // Import the new component
import defaultBusinessLogo from '../assets/default-business-logo.png';

// Google Places API key
const GOOGLE_API_KEY = 'AIzaSyBUKsrmx9qFndhqsS767pqnHDMgHrbcZvk';

interface Business {
  id: string;
  name: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  status: string;
  gmbId?: string;
  logoUrl?: string;
  businessInfo?: {
    gmbId?: string;
    [key: string]: any;
  };
}

interface PlaceResult {
  place_id: string;
  name: string;
  formatted_address: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const BusinessSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const LogoContainer = styled.div`
  flex-shrink: 0;
  order: 1; // Logo on the left
  
  @media (min-width: 768px) {
    order: 1; // Logo on the left
  }
`;

const BusinessLogo = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%; // Completely rounded
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: 'Change Logo';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 5px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DetailsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  order: 2; // Fields on the right
  
  @media (min-width: 768px) {
    order: 2; // Fields on the right
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ContactInfoContainer = styled.div`
  margin-top: 20px;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-top: 6px;
  font-size: 14px;
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-top: 6px;
  font-size: 14px;
`;

// Places dropdown
const PlacesDropdown = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const DropdownResults = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  display: ${props => props.isVisible ? 'block' : 'none'};
`;

const DropdownItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const BusinessProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Google Maps API loading state
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  // Form fields
  const [googlePlacesSearch, setGooglePlacesSearch] = useState('');
  const [googlePlacesId, setGooglePlacesId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  
  // Places API results
  const [placesResults, setPlacesResults] = useState<PlaceResult[]>([]);
  
  // UI states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPlacesDropdown, setShowPlacesDropdown] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Load Google Places API script
    const loadGoogleMapsScript = () => {
      if (!window.google && !document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('Google Maps API loaded');
          setGoogleMapsLoaded(true);
        };
        document.head.appendChild(script);
      } else if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
      }
    };

    loadGoogleMapsScript();
  }, []);

  useEffect(() => {
    const fetchUserAndBusinessData = async () => {
      if (!businessId) return;

      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);
        
        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          const businessData = {
            id: businessDoc.id,
            ...businessDoc.data()
          } as Business;
          
          setBusiness(businessData);
          setBusinessName(businessData.name || '');
          setBusinessEmail(businessData.email || '');
          setLogoUrl(businessData.logoUrl || '');
          
          // Set Google Places ID
          const gmbId = businessData.gmbId || 
                        (businessData.businessInfo && businessData.businessInfo.gmbId) || 
                        '';
          setGooglePlacesId(gmbId);
          setGooglePlacesSearch(businessData.name || '');
          
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndBusinessData();
  }, [businessId, navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
  
  const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setLogoFile(files[0]);
      setLogoUrl(URL.createObjectURL(files[0]));
    }
  };
  
  const handleGooglePlacesSearch = (query: string) => {
    setGooglePlacesSearch(query);
    
    // Clear previous timeout
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
    }

    if (!query) {
      setPlacesResults([]);
      setShowPlacesDropdown(false);
      return;
    }

    setPlacesLoading(true);
    setShowPlacesDropdown(true);
    
    // Add debounce to API calls
    autocompleteTimeoutRef.current = setTimeout(() => {
      if (!googleMapsLoaded || !window.google?.maps?.places) {
        console.error("Google Maps API not loaded");
        setPlacesLoading(false);
        setPlacesResults([]);
        return;
      }
      
      try {
        // Use classic AutocompleteService with callback
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          { 
            input: query,
            types: ['establishment']
          }, 
          (predictions, status) => {
            setPlacesLoading(false);
            
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setPlacesResults(
                predictions.map(prediction => ({
                  place_id: prediction.place_id,
                  name: prediction.structured_formatting.main_text,
                  formatted_address: prediction.structured_formatting.secondary_text
                }))
              );
            } else {
              setPlacesResults([]);
            }
          }
        );
      } catch (error) {
        console.error("Error with places autocomplete:", error);
        setPlacesLoading(false);
        setPlacesResults([]);
      }
    }, 300);
  };
  
  const handleGooglePlacesSelect = (placeId: string) => {
    if (!googleMapsLoaded || !window.google?.maps?.places) {
      setError("Google Places API not loaded");
      setShowPlacesDropdown(false);
      return;
    }
    
    try {
      // Create a PlacesService instance
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      
      // Use classic callback version
      placesService.getDetails(
        {
          placeId: placeId,
          fields: ['name', 'formatted_address', 'place_id']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            // Update fields with place data
            const businessNameValue = place.name || '';
            setBusinessName(businessNameValue);
            setGooglePlacesId(place.place_id || '');
            setGooglePlacesSearch(businessNameValue); // Also update the search field with the selected business name
            setShowPlacesDropdown(false);
          } else {
            setError("Could not retrieve place details");
          }
        }
      );
    } catch (error) {
      console.error("Error getting place details:", error);
      setError("Failed to get place details");
    }
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    
    if (!business || !businessId) return;

    if (!businessName.trim()) {
      setError('Business name is required');
      return;
    }

    if (!businessEmail.trim()) {
      setError('Business email is required');
      return;
    }

    try {
      setSaving(true);

      let updatedLogoUrl = logoUrl;

      // Upload logo if changed
      if (logoFile) {
        updatedLogoUrl = await uploadBusinessLogo(businessId, logoFile);
      }

      // Update business document
      const businessRef = doc(db, 'businesses', businessId);
      await updateDoc(businessRef, {
        name: businessName,
        email: businessEmail,
        logoUrl: updatedLogoUrl,
        gmbId: googlePlacesId,
        'businessInfo.gmbId': googlePlacesId,
        updatedAt: new Date()
      });

      setSuccess('Business profile updated successfully');
      
      // Update the local business state
      setBusiness(prev => {
        if (!prev) return null;
        return {
          ...prev,
          name: businessName,
          email: businessEmail,
          logoUrl: updatedLogoUrl,
          gmbId: googlePlacesId,
          businessInfo: {
            ...prev.businessInfo,
            gmbId: googlePlacesId
          }
        };
      });
    } catch (error) {
      console.error('Error updating business profile:', error);
      setError('Failed to update business profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <div>Loading...</div>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Business Profile</title>
        <meta name="description" content={`${APP_NAME} - Business Profile`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          
          <Helmet>
            <title>{`Profile - ${APP_NAME}`}</title>
          </Helmet>
          
          <PageTitle>Profile</PageTitle>
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={handleNavigateToSettings}>
              <BreadcrumbText>{business?.name}</BreadcrumbText>
            </BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Profile</span>
          </Breadcrumbs>
          
          <BusinessSection>
            <FormContainer>
              <ProfileHeader>
                <LogoContainer>
                  <BusinessLogo onClick={handleLogoClick}>
                    <img 
                      src={logoUrl || defaultBusinessLogo} 
                      alt="Business Logo" 
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = defaultBusinessLogo;
                      }} 
                    />
                  </BusinessLogo>
                  <HiddenFileInput 
                    type="file" 
                    accept="image/*" 
                    ref={fileInputRef} 
                    onChange={handleLogoChange} 
                  />
                </LogoContainer>
                
                <DetailsContainer>
                  <PlacesDropdown>
                    <PlaceSearchInput 
                      value={googlePlacesSearch}
                      onChange={(e) => handleGooglePlacesSearch(e.target.value)}
                      onFocusAction={() => setShowPlacesDropdown(!!googlePlacesSearch)}
                      placeholder="Search for your business on Google"
                      name="googlePlacesSearch"
                      label="Google Business Profile"
                      secondary
                    />
                    <DropdownResults isVisible={showPlacesDropdown}>
                      {placesLoading ? (
                        <DropdownItem>Loading...</DropdownItem>
                      ) : placesResults.length > 0 ? (
                        placesResults.map((place) => (
                          <DropdownItem 
                            key={place.place_id} 
                            onClick={() => handleGooglePlacesSelect(place.place_id)}
                          >
                            <strong>{place.name}</strong>
                            <div>{place.formatted_address}</div>
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem>No results found</DropdownItem>
                      )}
                    </DropdownResults>
                  </PlacesDropdown>
                  
                  <Input 
                    type="text"
                    value={googlePlacesId}
                    onChange={() => {}}
                    placeholder="Google Places ID"
                    name="googlePlacesId"
                    label="Google Places ID"
                    readOnly
                    secondary
                  />

                  <Input 
                    type="text"
                    value={businessId || ''}
                    onChange={() => {}}
                    placeholder="Merchant ID"
                    name="MerchantId"
                    label="Merchant ID"
                    readOnly
                    secondary
                  />
                </DetailsContainer>
              </ProfileHeader>

              <ContactInfoContainer>
                <Input 
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  placeholder="Enter business email"
                  name="businessEmail"
                  label="Business Email"
                  required
                  secondary
                  error={error && !businessEmail.trim() ? 'Business email is required' : undefined}
                />
              </ContactInfoContainer>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}

            </FormContainer>
              <ButtonContainer>
                <Button 
                  text="Save" 
                  onClick={handleSaveProfile} 
                  loading={saving}
                  bgColor="#000"
                  textColor="#fff"
                  width="auto"
                  minWidth="150px"
                />
              </ButtonContainer>
          </BusinessSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default BusinessProfilePage;